import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Search, Bell, Award, TrendingUp, Users, Globe, ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';
import BlogPageStyles from '../styles/BlogPageStyles';

const Blogs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');

  const navigate = useNavigate();

  const blogRoutes = {
    7: '/blog/architectural-design-2026',
    5: '/blog/seo-strategies-2026',
    6: '/blog/professional-website-2026',
    1: '/blog/ai-solutions-architecture',
    2: '/blog/mern-stack-2026',
    3: '/blog/microservices-architecture',
    8: '/blog/vibe-coding-2026',
    9: '/blog/vibe-coding-2-0'
  };

  const categories = ['All', 'Blogs', 'Press Release', 'Company News', 'Partnership', 'Recognition'];

  const blogArticles = [
    {
      id: 9,
      type: 'blog',
      category: 'AI & Technology',
      title: 'Vibe Coding 2.0: How AI-Assisted Development is Evolving in 2025',
      excerpt: 'Vibe Coding 2.0 goes beyond autocomplete — AI agents now plan, write, test, and deploy entire features autonomously. Discover the agentic tools, real-world impact numbers, and the new developer skill stack shaping software development in 2025.',
      author: 'Shivam Singh',
      date: 'September 16, 2026',
      image: 'Vibe Coding 2.0'
    },
    {
      id: 8,
      type: 'blog',
      category: 'AI & Technology',
      title: 'Vibe Coding in 2026: The AI-Assisted Development Revolution',
      excerpt: 'Vibe coding is taking the dev world by storm — describe what you want in plain English and AI writes the code. Explore the top tools like Cursor AI, GitHub Copilot Workspace, and Bolt.new, and learn how InitX Technologies uses vibe coding to ship faster.',
      author: 'Satyam Kumar',
      date: 'June 15, 2026',
      image: 'Vibe Coding 2026'
    },
    {
      type: 'blog',
      category: 'Architecture & Civil Design',
      title: 'How Technology is Transforming Architectural Design in 2026',
      excerpt: 'From AI-powered blueprints and 3D modeling to smart building systems, technology is revolutionizing architecture. Learn how InitX Technologies bridges the gap between civil engineering and digital innovation for residential and commercial projects.',
      author: 'Satyam Kumar',
      date: 'June 13, 2026',
      image: 'Architecture & Tech'
    },
    {
      id: 5,
      type: 'blog',
      category: 'SEO & Digital Marketing',
      title: 'Top 10 SEO Strategies to Rank Your Website in 2026',
      excerpt: 'Discover the most effective SEO techniques in 2026 — from Core Web Vitals optimization and semantic search to AI-driven content strategy and local SEO. Learn how InitX Technologies helps businesses dominate search rankings and drive organic traffic.',
      author: 'Pratyush Shrivastav',
      date: 'June 12, 2026',
      image: 'SEO Strategies 2026'
    },
    {
      id: 6,
      type: 'blog',
      category: 'Web Development',
      title: 'Why Your Business Needs a Professional Website in 2026',
      excerpt: 'In the digital-first world, a professional website is no longer optional. Explore how a well-designed, fast, and SEO-optimized website can boost your brand credibility, attract customers, and grow your business — with insights from InitX Technologies.',
      author: 'Vijay Kumar',
      date: 'June 11, 2026',
      image: 'Professional Website'
    },
    {
      id: 1,
      type: 'blog',
      category: 'AI & Technology',
      title: 'Building Scalable AI Solutions with Modern Architecture',
      excerpt: 'Explore how InitX Technologies leverages cutting-edge AI frameworks and cloud infrastructure to build enterprise-grade solutions.',
      author: 'Dr. Sarah Chen',
      date: 'June 10, 2026',
      image: 'AI Architecture'
    },
    {
      id: 2,
      type: 'blog',
      category: 'Tutorials',
      title: 'Complete Guide to MERN Stack Development in 2026',
      excerpt: 'A comprehensive tutorial covering MongoDB, Express.js, React, and Node.js with real-world project examples.',
      author: 'Mike Johnson',
      date: 'June 8, 2026',
      image: 'MERN Tutorial'
    },
    {
      id: 3,
      type: 'blog',
      category: 'Cloud Computing',
      title: 'Microservices Architecture: Best Practices and Implementation',
      excerpt: 'Learn how to design and implement scalable microservices using Docker, Kubernetes, and modern DevOps practices.',
      author: 'Alex Rodriguez',
      date: 'June 6, 2026',
      image: 'Microservices'
    }
  ];

  const newsItems = [
    {
      id: 1,
      type: 'news',
      category: 'Press Release',
      icon: <Award size={16} />,
      title: 'InitX Technologies Wins Best AI Innovation Award at TechSummit 2026',
      date: 'June 14, 2026',
      excerpt: 'InitX Technologies recognized for groundbreaking AI-powered business automation platform serving 200+ enterprises.',
    },
    {
      id: 2,
      type: 'news',
      category: 'Company News',
      icon: <TrendingUp size={16} />,
      title: 'InitX Achieves 300% Revenue Growth in FY 2025-26',
      date: 'June 9, 2026',
      excerpt: 'Record-breaking financial performance with revenue crossing $8M annually, driven by AI and cloud services.',
    },
    {
      id: 3,
      type: 'news',
      category: 'Partnership',
      icon: <Globe size={16} />,
      title: 'Strategic Partnership with Microsoft to Accelerate AI Adoption',
      date: 'June 7, 2026',
      excerpt: 'Partnership with Microsoft to help enterprises accelerate AI adoption through Azure cloud services.',
    },
    {
      id: 4,
      type: 'news',
      category: 'Expansion',
      icon: <Users size={16} />,
      title: 'InitX Opens New Development Center in Hyderabad',
      date: 'June 5, 2026',
      excerpt: 'New 50,000 sq ft facility will house 200+ engineers as AI research and development hub.',
    },
    {
      id: 5,
      type: 'news',
      category: 'Recognition',
      icon: <Award size={16} />,
      title: 'CEO Named Tech Leader of the Year by Business Today',
      date: 'June 3, 2026',
      excerpt: 'InitX CEO recognized for vision in driving digital transformation across industries.',
    },
    {
      id: 6,
      type: 'news',
      category: 'Product Launch',
      icon: <TrendingUp size={16} />,
      title: 'InitX Launches Revolutionary AI-Powered Analytics Platform',
      date: 'June 1, 2026',
      excerpt: 'New platform combines ML, NLP, and predictive analytics for automated decision-making.',
    }
  ];

  const allContent = [...blogArticles, ...newsItems];

  const filteredContent = allContent.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = activeFilter === 'All' || 
                         (activeFilter === 'Blogs' && item.type === 'blog') ||
                         (activeFilter !== 'Blogs' && item.category === activeFilter);
    return matchesSearch && matchesFilter;
  });

  const blogs = filteredContent.filter(item => item.type === 'blog');
  const news = filteredContent.filter(item => item.type === 'news');

  return (
    <div className="blogs-container">
      <BlogPageStyles />
      <SEO
        title="Blog | InitX Technologies"
        description="Read the latest insights from InitX Technologies on AI, cloud, software engineering, and digital transformation."
        keywords="technology blog, AI, cloud computing, software development, DevOps, digital transformation, tech insights, SEO strategies 2026, web development India, architectural design technology, MERN stack tutorial, InitX Technologies blog"
        type="website"
        siteName="InitX Technologies"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blogs' }
        ]}
      />
      <section className="blogs-hero">
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', marginBottom: '20px', position: 'relative', zIndex: 2 }}
        >
          Blogs & Latest News
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ fontSize: '1.2rem', opacity: 0.9, position: 'relative', zIndex: 2 }}
        >
          Stay updated with insights, tutorials, press releases, and company news from InitX Technologies
        </motion.p>
      </section>

      <div className="search-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search blogs and news..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Search size={20} className="search-icon" />
        </div>
        
        <div className="filter-buttons">
          {categories.map(category => (
            <button
              key={category}
              className={`filter-button ${activeFilter === category ? 'active' : ''}`}
              onClick={() => setActiveFilter(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div className="content-grid">
        <div className="main-content">
          {(activeFilter === 'All' || activeFilter === 'Blogs') && blogs.length > 0 && (
            <div>
              <h2 className="section-title">Latest Blog Articles</h2>
              <div className="articles-grid">
                {blogs.map((article, index) => (
                  <motion.article
                    key={article.id}
                    className="article-card"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <div className="article-image">{article.image}</div>
                    <div className="article-content">
                      <span className="article-category">{article.category}</span>
                      <h3 className="article-title">{article.title}</h3>
                      <div className="article-meta">
                        <span className="meta-flex">
                          <User size={14} />
                          {article.author}
                        </span>
                        <span className="meta-flex">
                          <Calendar size={14} />
                          {article.date}
                        </span>
                      </div>
                      <p className="article-excerpt">{article.excerpt}</p>
                      <button className="read-more-button" onClick={() => navigate(blogRoutes[article.id] || '/blogs')}>
                        Read More <ArrowRight size={16} />
                      </button>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          )}

          {(activeFilter === 'All' || activeFilter !== 'Blogs') && news.length > 0 && (
            <div>
              <h2 className="section-title">Latest News & Press Releases</h2>
              <div className="news-grid">
                {news.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="news-card"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div className="card-padding">
                      <div className="news-type">
                        {item.icon}
                        {item.category}
                      </div>
                      <h4 className="news-title">{item.title}</h4>
                      <div className="news-date">
                        <Calendar size={14} />
                        {item.date}
                      </div>
                      <p className="news-excerpt">{item.excerpt}</p>
                      <button className="news-button" onClick={() => alert(`Reading: ${item.title}`)}>
                        <ExternalLink size={12} />
                        Read Full Story
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="sidebar">
          <div className="newsletter-card">
            <Bell size={32} style={{ marginBottom: '20px' }} />
            <h3 style={{ marginBottom: '15px' }}>Stay Updated</h3>
            <p style={{ marginBottom: '20px', opacity: 0.9, fontSize: '14px' }}>
              Get the latest blogs, news, and updates delivered to your inbox
            </p>
            <input
              type="email"
              placeholder="Your email"
            />
            <button onClick={() => alert('Newsletter subscription coming soon!')}>
              Subscribe
            </button>
          </div>

          <div className="sidebar-card">
            <h3>Quick Links</h3>
            <div className="button-group">
              <button onClick={() => setActiveFilter('All')}>
                All Content
              </button>
              <button onClick={() => setActiveFilter('Blogs')}>
                Blog Articles
              </button>
              <button onClick={() => setActiveFilter('Press Release')}>
                Press Releases
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Blogs;
